import React, { memo, useEffect, useRef, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { useHoldRepeat } from "./useHoldRepeat";

/**
 * ChainFlow OS · PremiumStepper
 * ────────────────────────────────────────────────────────────
 * The brand-level operational numeric control. Wraps the
 * `useHoldRepeat` primitive with the locked v1 visual identity so
 * every stepper across the platform looks and feels identical.
 *
 * Where to use it (apply progressively across the platform):
 *  • Mission Control bulk-action controls
 *  • Director Centre range filters
 *  • Forecasting configuration inputs
 *  • Revenue threshold controls
 *  • Operational policy settings
 *  • Exposure audit controls (shipped)
 *  • Any future operational stepper
 *
 * Visual identity (v1, locked):
 *  • rounded-xl light surface, slate-200 border, inset top highlight
 *  • violet focus-within ring (operational accent)
 *  • icon-only square buttons inside the field
 *  • centered tabular-numeric value with subtle crossfade on change
 *  • disabled state at bounds (opacity-30, repeat halts)
 *  • reduced-motion aware
 *
 * Props:
 *  @prop {number} value
 *  @prop {(next:number) => void} onChange
 *  @prop {number} lo
 *  @prop {number} hi
 *  @prop {number} [step=1]
 *  @prop {string} [prefix]  e.g. "£"
 *  @prop {string} [suffix]  e.g. "%", " days"
 *  @prop {"sm"|"md"|"lg"} [size="md"]
 *  @prop {string} [testid]  Used to derive `${testid}`, `${testid}-dec`,
 *                            `${testid}-inc` on the value + buttons.
 *  @prop {string} [className]
 *  @prop {(v:number) => string} [format]  Custom display formatter.
 */
export const PremiumStepper = memo(function PremiumStepper({
  value,
  onChange,
  lo,
  hi,
  step = 1,
  prefix = "",
  suffix = "",
  size = "md",
  testid,
  className = "",
  format,
}) {
  const valueRef = useRef(value);
  valueRef.current = value;

  const { getBindings } = useHoldRepeat({
    getValue: () => valueRef.current,
    onChange,
    lo,
    hi,
    step,
  });

  // Pulse key drives the calm crossfade on the value display.
  const [pulse, setPulse] = useState(0);
  useEffect(() => { setPulse((p) => p + 1); }, [value]);

  const atMin = value <= lo;
  const atMax = value >= hi;

  const sizing = {
    sm: { wrap: "px-1.5 py-1 gap-1",   btn: "w-6 h-6",   icon: 11, text: "text-[14px]" },
    md: { wrap: "px-2 py-1.5 gap-1",   btn: "w-7 h-7",   icon: 13, text: "text-[18px]" },
    lg: { wrap: "px-2.5 py-2 gap-1.5", btn: "w-8 h-8",   icon: 14, text: "text-[20px]" },
  }[size] || { wrap: "px-2 py-1.5 gap-1", btn: "w-7 h-7", icon: 13, text: "text-[18px]" };

  const btnBase =
    `relative ${sizing.btn} rounded-lg inline-flex items-center justify-center select-none ` +
    "text-slate-600 transition-all duration-150 active:scale-[0.92] " +
    "disabled:opacity-30 disabled:cursor-not-allowed " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60";

  const decBind = getBindings(-1);
  const incBind = getBindings(+1);

  const display =
    format ? format(value) :
    `${prefix}${value.toLocaleString()}${suffix}`;

  return (
    <div
      className={`cf-stepper rounded-xl bg-white border border-slate-200 flex items-center ${sizing.wrap} ${className}`}
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)" }}
      data-testid={testid ? `${testid}-wrap` : undefined}
    >
      <button
        type="button"
        aria-label="Decrease"
        disabled={atMin}
        className={btnBase + " hover:bg-slate-100"}
        data-testid={testid ? `${testid}-dec` : undefined}
        {...decBind}
      >
        <Minus size={sizing.icon} strokeWidth={2.5} />
      </button>
      <span
        key={pulse}
        className={`cf-stepper-value font-display font-bold text-slate-900 tabular-nums flex-1 text-center ${sizing.text}`}
        data-testid={testid}
      >
        {display}
      </span>
      <button
        type="button"
        aria-label="Increase"
        disabled={atMax}
        className={btnBase + " hover:bg-slate-100"}
        data-testid={testid ? `${testid}-inc` : undefined}
        {...incBind}
      >
        <Plus size={sizing.icon} strokeWidth={2.5} />
      </button>
      <style>{`
        .cf-stepper { transition: border-color 180ms ease, box-shadow 180ms ease; }
        .cf-stepper:focus-within {
          border-color: rgba(124,58,237,0.45);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 0 0 3px rgba(124,58,237,0.10);
        }
        .cf-stepper-value {
          animation: cf-stepper-pulse 220ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform, opacity;
        }
        @keyframes cf-stepper-pulse {
          0%   { transform: translateY(2px); opacity: 0.55; }
          100% { transform: translateY(0);   opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-stepper-value { animation: none !important; }
        }
      `}</style>
    </div>
  );
});

export default PremiumStepper;
